import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutState } from '../../Context/LayoutContext/LayoutContext';
import { useTranslation } from 'react-i18next';

// SVG Chevron components with larger size
const ChevronDown = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const ChevronUp = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

// Helper function to resolve link paths
const resolveLinkPath = (childTo, parentTo) => `${parentTo}/${childTo}`;

// Helper function to get user roles
const getUserRoles = () => {
  let userobjdata = JSON.parse(localStorage.getItem('user_obj_data'));
  let userRealmRoles = [];
  try {
    userRealmRoles = userobjdata.tokenParsed.resource_access['realm-management'].roles;
  } catch (e) {
    console.log(e);
  }
  return userRealmRoles;
};

const NavItemHeader = (props) => {
  const { t } = useTranslation();
  const { item } = props;
  const { label, icon, to: headerToPath, children } = item;
  const location = useLocation();
  const { collapsed } = LayoutState();

  // Determine if current path matches this nav item or any of its children
  const isActive = location.pathname.includes(headerToPath);

  const [expanded, setExpand] = useState(isActive);

  const onExpandChange = (e) => {
    e.preventDefault();
    setExpand((expanded) => !expanded);
  };

  // Determine the navigation path for the parent item:
  // If collapsed and children exist, navigate to first child's path
  const parentNavPath = collapsed && children && children.length > 0
    ? resolveLinkPath(children[0].to, headerToPath)
    : headerToPath;

  // Handle click on parent item
  const handleParentClick = (e) => {
    if (!collapsed && children && children.length > 0) {
      // Expanded menu: prevent navigation and toggle expand/collapse
      e.preventDefault();
      onExpandChange(e);
    }
    // Collapsed menu: allow navigation to first child (no preventDefault)
  };

  // Translation for label
  const translatedLabel = typeof label === 'string' ? t(label) : label;

  // Styles helper function
  const getNavStyles = (isItemActive, isChildItem) => {
    let baseClass = "nav__link";

    if (isItemActive) {
      if (isChildItem) {
        baseClass += collapsed
          ? " child__nav__active--collapsed"
          : " child__nav__active";
      } else {
        baseClass += collapsed
          ? " nav__active--collapsed"
          : " nav__active";
      }
    }

    if (isChildItem) {
      baseClass += " child-menu";
    }

    return baseClass;
  };

  return (
    <>
      <li className="nav__item">
        <div className={`nav-item-with-arrow ${collapsed ? "nav-item-collapsed" : ""}`}>
          <NavLink
            to={parentNavPath}
            title={collapsed ? translatedLabel : ""}
            className={({ isActive: linkActive }) =>
              getNavStyles(linkActive || isActive, item.children?.length === 0)}
            onClick={handleParentClick}
          >
            <span className="nav-icon">{icon}</span>
            {!collapsed && translatedLabel}
          </NavLink>

          {!collapsed && children && children.length > 0 && (
            <span
              className={`arrow-icon ${isActive ? "active-arrow" : ""}`}
              onClick={onExpandChange}
            >
              {expanded ? <ChevronUp /> : <ChevronDown />}
            </span>
          )}
        </div>
      </li>

      {expanded && !collapsed && (
        <div className="nav-children-container">
          {children.map((item, index) => {
            const key = `${item.label}-${index}`;
            const childPath = resolveLinkPath(item.to, props.item.to);

            if (item.children) {
              return (
                <div key={key}>
                  <NavItemHeader
                    item={{
                      ...item,
                      to: childPath,
                    }}
                  />
                </div>
              );
            }

            if (item.allowedRoles) {
              const userRoles = getUserRoles();
              if (!item.allowedRoles.some(ai => userRoles.includes(ai))) {
                return null;
              }
            }

            const childTranslatedLabel = typeof item.label === 'string' ? t(item.label) : item.label;

            return (
              <li key={key} className="nav__item">
                <NavLink
                  to={childPath}
                  className={({ isActive }) => getNavStyles(isActive)}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {!collapsed && childTranslatedLabel}
                </NavLink>
              </li>
            );
          })}
        </div>
      )}
    </>
  );
};

export default NavItemHeader;
